import { useCallback } from "react";
import { toast } from "react-toastify";
import { getTokens } from "../common/manage_tokens";
import { getRateLimitRejectionFromResponse } from "../common/rateLimit";
import { notifyRateLimited } from "../common/rateLimitNotice";
import { expireSession, isSessionExpiredResponse } from "../common/session";
import { config } from "../config";

export const useDownloadCsv = (path: string, data: any) => {
    const downloadCsv = useCallback(async () => {
        const url = `${config.apiBaseUrl}/${path}`;

        const token = getTokens();

        let response: Response;

        try {
            response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(data)
            });
        } catch {
            toast.error("Could not reach the server to export the CSV.");
            return;
        }

        // The status has to be checked before the body is touched. These
        // exports are capped at 5 per minute, and a rejection is a JSON error
        // body — saving it blind writes the error to disk as `export.csv`,
        // which looks like a successful download of a corrupt file.
        if (!response.ok) {
            const rejection = await getRateLimitRejectionFromResponse(response);

            if (rejection) {
                notifyRateLimited(rejection);
                return;
            }

            // Not a GraphQL call, so the Apollo error link never sees this.
            if (await isSessionExpiredResponse(response)) {
                expireSession();
                return;
            }

            toast.error(`Could not export the CSV (HTTP ${response.status}).`);
            return;
        }

        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = objectUrl;
        link.setAttribute('download', `export.csv`);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
    }, [path, data]);

    return downloadCsv;
}
